<template>
    <div class="section-card">
        <div class="card-content">
            <div class="header-row">
                <div>
                    <h2 class="title">Signed Webhooks</h2>
                    <p class="help">
                        This is an optional layer of security so that you can verify that
                        a request is coming from your service and its payload has not been
                        tampered with during transit.
                    </p>
                </div>
            </div>
            <div class="field">
                <div class="signature-row">
                    <div class="label">Signature Secret</div>
                </div>
                <div class="sublabel">
                    Each incoming webhook request includes a JWT token in the
                    authorization header, signed using your signature secret.
                </div>
                <div class="secret-row-key">
                    <div class="secret-row">
                        <VTextField :type="showSecret ? 'text' : 'password'" :modelValue="form.secret" :readonly="true" appearance="ghost" class="secret-input" placeholder="Enter a secret" />
                        <VButton appearance="ghost" size="condensed" :disabled="!form.secret" @click="showSecret = !showSecret">
                            <template #icon><VIcon :name="showSecret ? eyeOutline : eyeOffOutline" /></template>
                        </VButton>
                        <VButton appearance="ghost" size="condensed" :disabled="!form.secret" @click="copyToClipboard(form.secret)">
                            <template #icon><VIcon :name="copyOutline" /></template>
                        </VButton>
                    </div>
                    <span class="refresh" @click="() => (showConfirmModal = true)">
                        Refresh Secret
                    </span>
                </div>
            </div>

            <div class="field extra">
                <div class="label">Signature Method</div>
                <div class="sublabel">
                    The method will define the algorithm used to create the signature
                </div>
                <VSelect v-model="form.hashing_method" appearance="ghost" class="method-select" fixed-dropdown :disabled="loading">
                    <VOption v-for="method in SignatureMethods" :key="method.value" :value="method.value" :text="method.label" />
                </VSelect>
            </div>
            <div v-if="isEditing" class="actions">
                <VButton class="vvd-btn-cancel" appearance="filled" label="Cancel" :disabled="loading" @click="cancelEdit" />
                <VButton class="vvd-btn-primary" connotation="cta" appearance="filled" label="Save" :disabled="loading" @click="refreshMethod" />
            </div>
            <div v-if="loading" class="overlay">
                <VProgressRing connotation="cta" />
            </div>
        </div>
        <CustomWarningModal :isOpen="showConfirmModal" heading="Are you sure you want to refresh the secret?"
            description="Refreshing the secret will invalidate the previous one." cancelText="Cancel"
            confirmText="Refresh Secret" @cancel="showConfirmModal = false" @confirm="refreshSecret" />
    </div>
</template>

<script>
import CustomWarningModal from "@/components/CustomWarningModal.vue";
import apiInterceptor from "@/shared/apiInterceptor";
import { useUIStore } from '@/stores/ui';
import { useClipboard } from '@/composables/useClipboard';
import { environment } from '@/shared/env.js';

const SignatureMethods = [
    { label: "MD5 Hash signature", value: "HASH_MD5" },
    { label: "MD5 HMAC signature", value: "HMAC_MD5" },
    { label: "SHA1 HMAC signature", value: "HMAC_SHA1" },
    { label: "SHA-256 HMAC signature", value: "HMAC_SHA256" },
    { label: "SHA-512 HMAC signature", value: "HMAC_SHA512" },
];
export default {
    name: "SignedWebhookCard",
    components: { CustomWarningModal },
    setup() {
        const { copyToClipboard } = useClipboard();
        return { copyToClipboard };
    },
    data() {
        return {
            SignatureMethods,
            eyeOutline: 'eye-line',
            eyeOffOutline: 'eye-hide-line',
            copyOutline: 'copy-2-line',
            loading: false,
            showSecret: false,
            showConfirmModal: false,
            original: { secret: "", hashing_method: "HMAC_SHA256" },
            form: { secret: "", hashing_method: "HMAC_SHA256" },
        };
    },
    computed: {
        isEditing() {
            return this.form.secret !== this.original.secret || this.form.hashing_method !== this.original.hashing_method;
        },
    },
    mounted() { this.fetchConfig(); },
    methods: {
        async fetchConfig() {
            this.loading = true;
            try {
                const resp = await apiInterceptor.get(environment("VUE_APP_APIGEE_API_URL") + "webhooks/secret");
                const cfg = resp || {};
                this.original = { secret: cfg.secret || "", hashing_method: cfg.hashing_method || "HMAC_SHA256" };
                this.form = { ...this.original };
            } catch (e) {
                useUIStore().updateToast({ open: true, message: e?.response?.data?.detail || "Failed to load signature settings", type: "danger" });
            } finally { this.loading = false; }
        },
        async refreshSecret() {
            useUIStore().updateLoading({ isLoading: true, message: 'Refreshing webhook secret...' });
            try {
                const res = await apiInterceptor.post(environment("VUE_APP_APIGEE_API_URL") + `webhooks/secret/refresh`);
                this.original = { secret: res.secret, hashing_method: res.hashing_method };
                this.form = { secret: res.secret, hashing_method: res.hashing_method };
                useUIStore().updateToast({ open: true, message: "Webhook secret updated successfully.", type: "success" });
                this.showConfirmModal = false;
            } catch (e) {
                useUIStore().updateToast({ open: true, message: e?.response?.data?.detail || "Failed to update settings", type: "danger" });
            } finally { useUIStore().updateLoading({ isLoading: false }); }
        },
        async refreshMethod() {
            useUIStore().updateLoading({ isLoading: true, message: 'Updating webhook method...' });
            try {
                const res = await apiInterceptor.put(environment("VUE_APP_APIGEE_API_URL") + `webhooks/secret/hashing-method`, { hashing_method: this.form.hashing_method });
                this.original = { secret: res.secret, hashing_method: res.hashing_method };
                this.form = { secret: res.secret, hashing_method: res.hashing_method };
                useUIStore().updateToast({ open: true, message: "Webhook method updated successfully.", type: "success" });
            } catch (e) {
                useUIStore().updateToast({ open: true, message: e?.response?.data?.detail || "Failed to update settings", type: "danger" });
            } finally { useUIStore().updateLoading({ isLoading: false }); }
        },
        cancelEdit() { this.form = { ...this.original }; },
    },
};
</script>

<style scoped lang="scss">
.section-card {
    border-radius: 12px;
    border: 1px solid var(--background-primary);
    box-shadow: none;
    background: var(--white);
    position: relative;
}
.card-content { padding: 20px; }
.title { font-size: 18px; font-weight: 700; color: var(--black); margin: 0 0 5px; }
.help { margin-top: 10px; color: var(--black); font-size: 12px; font-weight: 400; }
.header-row { display: flex; align-items: flex-start; justify-content: space-between; border-bottom: 1px solid var(--background-primary); padding-bottom: 15px; }
.field { margin: 18px 0; }
.field.extra { margin-bottom: 0px; }
.signature-row { display: flex; align-items: flex-start; justify-content: space-between; }
.label { font-size: 14px; font-weight: 700; margin-bottom: 8px; color: var(--black); }
.sublabel { font-size: 12px; color: var(--black); margin-bottom: 10px; }
.secret-row-key { display: flex; align-items: center; gap: 6px; color: var(--purple); }
.secret-row { display: flex; align-items: center; gap: 4px; border-bottom: 1px solid var(--background-primary); padding: 0 6px 0 0; background-color: var(--white); width: 60%; }
.secret-input { flex: 1; background: transparent; }
.method-select { width: 30%; background: var(--background-primary); border-radius: 5px; min-height: 40px; }
.actions { display: flex; gap: 10px; margin-top: 16px; }
.overlay { position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; background: rgba(255, 255, 255, 0.6); border-radius: 12px; }
.refresh { font-weight: 600; cursor: pointer; &:hover { text-decoration: underline; } }
</style>
